import { initializeApp } from 'firebase/app';
import type { Messaging } from 'firebase/messaging';
import { deleteToken, getMessaging, getToken } from 'firebase/messaging';
import { firebaseConfig } from '@/config/firebase';
import type { NotificationPermissionStatus } from './types';

export const FCM_SOURCE_EXPRESS_NOT_TAKEN = 'express_not_taken';

const firebaseApp = initializeApp(firebaseConfig, 'fcm-app');
let messagingInstance: Messaging | null = null;

export const getMessagingInstance = (): Messaging | null => {
  if (!isNotificationSupported()) {
    return null;
  }

  if (!messagingInstance) {
    messagingInstance = getMessaging(firebaseApp);
  }

  return messagingInstance;
};

export const isNotificationSupported = (): boolean => {
  return 'Notification' in window && 'serviceWorker' in navigator;
};

export const getNotificationPermissionStatus = (): NotificationPermissionStatus => {
  if (!isNotificationSupported()) {
    return 'unsupported';
  }

  return Notification.permission as NotificationPermissionStatus;
};

export const requestNotificationPermission = async (): Promise<NotificationPermissionStatus> => {
  if (!isNotificationSupported()) {
    return 'unsupported';
  }

  try {
    const permission = await Notification.requestPermission();
    return permission as NotificationPermissionStatus;
  } catch (error) {
    console.error('Error requesting notification permission:', error);
    throw error;
  }
};

export const registerServiceWorker = async (): Promise<ServiceWorkerRegistration | null> => {
  if (!('serviceWorker' in navigator)) {
    return null;
  }

  if (import.meta.env.VITE_ENV === 'test') {
    return null;
  }

  try {
    const existingRegistrations = await navigator.serviceWorker.getRegistrations();

    for (const reg of existingRegistrations) {
      if (reg.active?.scriptURL && !reg.active.scriptURL.includes('firebase-messaging-sw')) {
        await reg.unregister();
      }
    }

    const swUrl = import.meta.env.DEV ? '/dev-sw.js?dev-sw' : '/firebase-messaging-sw.js';

    const registration = await navigator.serviceWorker.register(swUrl, {
      scope: '/',
      type: import.meta.env.DEV ? 'module' : 'classic',
    });

    await navigator.serviceWorker.ready;

    if (registration.waiting) {
      registration.waiting.postMessage({ type: 'SKIP_WAITING' });
    }

    return registration;
  } catch (error) {
    console.error('Service Worker registration failed:', error);
    throw error;
  }
};

export const getFCMToken = async (
  serviceWorkerRegistration?: ServiceWorkerRegistration
): Promise<string | null> => {
  const messaging = getMessagingInstance();

  if (!messaging) {
    return null;
  }

  const vapidKey = import.meta.env.VITE_FIREBASE_VAPID_KEY as string;

  if (!vapidKey) {
    console.error('VAPID key not found');
    return null;
  }

  if (typeof vapidKey !== 'string' || vapidKey.length === 0) {
    console.error('VAPID key is invalid');
    return null;
  }

  if (!serviceWorkerRegistration) {
    console.error('Service Worker registration required');
    return null;
  }

  try {
    const token = await getToken(messaging, {
      vapidKey: vapidKey.trim(),
      serviceWorkerRegistration,
    });

    return token || null;
  } catch (error) {
    console.error('Error getting FCM token:', error);
    console.error('VAPID key length:', vapidKey?.length);
    console.error('VAPID key first chars:', vapidKey?.substring(0, 10));
    throw error;
  }
};

export const deleteFCMToken = async (): Promise<boolean> => {
  const messaging = getMessagingInstance();

  if (!messaging) {
    return false;
  }

  try {
    await deleteToken(messaging);
    return true;
  } catch (error) {
    console.error('Error deleting FCM token:', error);
    return false;
  }
};
